import { useEffect, useState } from "react";
import {
  Megaphone, ShieldAlert, Send, Loader2, X, ChevronDown, HardHat, Check, Trash2,
} from "lucide-react";
import api from "@/lib/api";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

const PRIORITY = {
  low: "border-white/25 text-white/50",
  normal: "border-sky-400/50 text-sky-300",
  high: "border-amber-400/50 text-amber-300",
  urgent: "border-red-500/50 text-red-300",
};

export function AlertsPanel() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", message: "", priority: "normal" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/fieldclaim/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const issue = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/fieldclaim/alerts", form); setForm({ title: "", message: "", priority: "normal" }); setOpen(false); load(); }
    finally { setBusy(false); }
  };
  const dismiss = async (id) => { await api.delete(`/fieldclaim/alerts/${id}`); load(); };

  return (
    <div className="border border-gold-500/25 bg-navy-900/40" data-testid="alerts-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gold-500/15">
        <Mono className="text-[0.55rem] text-gold-400/80 flex items-center gap-1.5"><Megaphone className="h-3 w-3" /> Field alerts</Mono>
        <button onClick={() => setOpen((o) => !o)} data-testid="new-alert-btn" className="inline-flex items-center gap-1.5 text-gold-400 hover:text-gold-300 text-xs"><Send className="h-3.5 w-3.5" /> Issue alert</button>
      </div>
      {open && (
        <form onSubmit={issue} className="p-4 space-y-3 border-b border-gold-500/15 bg-navy-950/40" data-testid="alert-form">
          <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Alert title (e.g. Priority change — do S2 first)" data-testid="alert-title" className={inp} />
          <textarea rows={2} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder="Details for field crews…" data-testid="alert-message" className={inp + " resize-none"} />
          <div className="flex items-center gap-3">
            <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} data-testid="alert-priority" className={inp + " cursor-pointer flex-1"}>
              {["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p} className="bg-navy-900 capitalize">{p}</option>)}
            </select>
            <button type="submit" disabled={busy} data-testid="send-alert-btn" className="bg-gold-500 text-navy-950 font-semibold px-4 py-2.5 rounded-sm hover:bg-gold-400 transition-colors flex items-center gap-2 text-sm disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send</button>
          </div>
        </form>
      )}
      {alerts.length === 0 ? (
        <div className="px-4 py-5 text-center text-white/35 text-xs font-light">No active alerts.</div>
      ) : (
        <div className="divide-y divide-gold-500/10 max-h-64 overflow-y-auto">
          {alerts.map((a) => (
            <div key={a.id} className="px-4 py-3 flex items-start gap-3" data-testid={`alert-${a.id}`}>
              <span className={`font-mono text-[0.5rem] uppercase tracking-wider px-1.5 py-0.5 border shrink-0 mt-0.5 ${PRIORITY[a.priority]}`}>{a.priority}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white">{a.title}</div>
                {a.message && <div className="text-white/55 text-xs font-light mt-0.5">{a.message}</div>}
                <Mono className="text-[0.45rem] text-white/35 mt-1 block flex items-center gap-1"><Check className="h-2.5 w-2.5" /> {a.ack_count} acknowledged</Mono>
              </div>
              <button onClick={() => dismiss(a.id)} data-testid={`dismiss-alert-${a.id}`} className="text-white/25 hover:text-red-400 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SwmsPanel({ org, onToggleRequired }) {
  const [swms, setSwms] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/fieldclaim/swms").then((r) => setSwms(r.data)).catch(() => {}); }, []);

  const toggle = async () => {
    setSaving(true);
    try { await api.patch("/fieldclaim/org/settings", { swms_required: !org?.swms_required }); onToggleRequired && onToggleRequired(); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-gold-500/25 bg-navy-900/40" data-testid="swms-panel">
      <div className="px-4 py-3 border-b border-gold-500/15 flex items-center justify-between gap-3">
        <Mono className="text-[0.55rem] text-gold-400/80 flex items-center gap-1.5"><ShieldAlert className="h-3 w-3" /> SWMS · high-risk activities</Mono>
        {org?.is_owner && (
          <button onClick={toggle} disabled={saving} data-testid="swms-required-toggle" className="flex items-center gap-2 group" title="Require crews to acknowledge SWMS before starting field activities">
            <span className={`h-4 w-7 rounded-full transition-colors relative ${org?.swms_required ? "bg-gold-500" : "bg-navy-800 border border-gold-500/20"}`}>
              <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${org?.swms_required ? "left-3.5" : "left-0.5"}`} />
            </span>
            <span className="text-[0.6rem] text-white/60 group-hover:text-white">{org?.swms_required ? "Mandatory" : "Optional"}</span>
          </button>
        )}
      </div>
      {org?.is_owner && (
        <div className="px-4 py-2 text-[0.6rem] text-white/40 font-light border-b border-gold-500/10" data-testid="swms-required-hint">
          {org?.swms_required ? "Field crews must acknowledge the relevant SWMS before starting an activity." : "Crews can view SWMS anytime; acknowledgement is not enforced."}
        </div>
      )}
      <div className="divide-y divide-gold-500/10 max-h-72 overflow-y-auto">
        {swms.map((s) => (
          <div key={s.id} data-testid={`swms-${s.code || s.id}`}>
            <button onClick={() => setExpanded((e) => (e === s.id ? null : s.id))} className="w-full text-left px-4 py-3 flex items-center gap-2.5 hover:bg-navy-800/60 transition-colors">
              <HardHat className={`h-4 w-4 shrink-0 ${s.high_risk ? "text-red-400" : "text-gold-500/60"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{s.title}</div>
                <Mono className="text-[0.45rem] text-white/40">{s.risk_level} risk{s.high_risk ? " · high-risk" : ""}</Mono>
              </div>
              <ChevronDown className={`h-4 w-4 text-white/30 transition-transform ${expanded === s.id ? "rotate-180" : ""}`} />
            </button>
            {expanded === s.id && (
              <div className="px-4 pb-4 pt-1 space-y-3 text-xs" data-testid={`swms-detail-${s.code || s.id}`}>
                {s.activity && <p className="text-white/55 font-light">{s.activity}</p>}
                {s.hazards?.length > 0 && <div><Mono className="text-[0.45rem] text-red-300/80 block mb-1">Hazards</Mono><ul className="space-y-1">{s.hazards.map((h, i) => <li key={i} className="text-white/65 flex gap-1.5"><span className="text-red-400">·</span>{h}</li>)}</ul></div>}
                {s.controls?.length > 0 && <div><Mono className="text-[0.45rem] text-emerald-300/80 block mb-1">Controls</Mono><ul className="space-y-1">{s.controls.map((c, i) => <li key={i} className="text-white/65 flex gap-1.5"><span className="text-emerald-400">✓</span>{c}</li>)}</ul></div>}
                {s.ppe?.length > 0 && <div className="text-white/50">PPE: {s.ppe.join(", ")}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const inp = "w-full bg-navy-950 border border-gold-500/20 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-gold-500";
